import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import InnerLayout from "../../components/layouts/inner-layout";
import Pagination from "../../components/tables/pagination1";
import FollowUpSummaryCards from "../../components/customer-follow-up/follow-up-summary-cards";
import FollowUpToolbar from "../../components/customer-follow-up/follow-up-toolbar";
import FollowUpTable from "../../components/customer-follow-up/follow-up-table";
import FollowUpDetailsPanel from "../../components/customer-follow-up/follow-up-details-panel";
import { useFollowUpList } from "../../components/customer-follow-up/use-follow-up-list";
import { followUpSettingsApi, followUpStatusApi } from "../../redux/apis/follow-up-api";
import { storeDetailsAction } from "../../redux/actions/invoices-action";
import {
  FOLLOW_UP_STATUS,
  buildFollowUpMessage,
  buildWhatsAppUrl,
  openExternalUrl,
} from "../../utils/follow-up";
import successToast from "../../components/helper-components/success-toast";
import errorToast from "../../components/helper-components/error-toast";
import warningToast from "../../components/helper-components/warning-toast";

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const INITIAL_FILTERS = { status: "pending", due: "all", fromDate: "", toDate: "" };

const CustomerFollowUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // rfu_id -> the action running on it ("status")
  const [busyActions, setBusyActions] = useState({});
  const [detailsFollowUp, setDetailsFollowUp] = useState(null);
  const [messageTemplate, setMessageTemplate] = useState("");
  const [storeName, setStoreName] = useState("");

  const { followUps, total, summary, isLoading, loadError, reload, updateFollowUp } =
    useFollowUpList({ filters, search, currentPage, pageSize });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    followUpSettingsApi()
      .then((res) => setMessageTemplate(res?.data?.data?.whatsapp_message ?? ""))
      .catch(() =>
        warningToast("Couldn't load the WhatsApp message from settings; the default message will be used")
      );
    // without the store details the message says "our store"
    dispatch(
      storeDetailsAction(
        (data) => setStoreName(data?.data?.store_name ?? ""),
        () => setStoreName("")
      )
    );
  }, [dispatch]);

  const changeFilters = (changes) => {
    setFilters((previous) => ({ ...previous, ...changes }));
    setCurrentPage(1);
  };

  const setBusy = (rfuId, action) =>
    setBusyActions((previous) => {
      const { [rfuId]: _finished, ...rest } = previous;
      return action ? { ...rest, [rfuId]: action } : rest;
    });

  // the change on the list row and on the open details panel
  const applyChanges = (rfuId, changes) => {
    updateFollowUp(rfuId, changes);
    setDetailsFollowUp((previous) =>
      previous?.rfu_id === rfuId
        ? { ...previous, ...(typeof changes === "function" ? changes(previous) : changes) }
        : previous
    );
  };

  const handleToggleStatus = async (followUp) => {
    const status =
      followUp.rfu_status === FOLLOW_UP_STATUS.done
        ? FOLLOW_UP_STATUS.pending
        : FOLLOW_UP_STATUS.done;
    setBusy(followUp.rfu_id, "status");
    try {
      const res = await followUpStatusApi({ rfu_id: followUp.rfu_id, status });
      applyChanges(followUp.rfu_id, { rfu_status: status });
      successToast(res?.data?.message ?? "Follow-up updated");
      // the counts change, and the follow-up may leave the current status tab
      reload({ silent: true });
    } catch (error) {
      errorToast(error?.response?.data?.message ?? "Couldn't update the follow-up");
    } finally {
      setBusy(followUp.rfu_id, null);
    }
  };

  // items: the ones ticked in the details panel
  const handleWhatsApp = (followUp, items) => {
    if (items.length === 0) {
      warningToast("Tick the items to send");
      return;
    }
    const message = buildFollowUpMessage({
      template: messageTemplate,
      followUp,
      items,
      storeName,
    });
    const url = buildWhatsAppUrl(followUp.cmr_phone_number, message);
    if (!url) {
      errorToast("This customer has no valid phone number");
      return;
    }
    openExternalUrl(url);
  };

  const handleCommentAdded = (rfuId, comment) =>
    applyChanges(rfuId, (followUp) => ({
      comments_count: (followUp?.comments_count ?? 0) + 1,
      last_comment: comment?.rfc_comment ?? followUp?.last_comment,
    }));

  return (
    <InnerLayout title="Customer Follow-up" list={true}>
      {/* Same layout as the other list pages: summary cards and filters on top,
          the table filling the rest with the pagination under it */}
      <div className="flex h-full flex-col">
        <div className="shrink-0">
          <FollowUpSummaryCards summary={summary} filters={filters} onSelect={changeFilters} />
          <FollowUpToolbar
            filters={filters}
            onFiltersChange={changeFilters}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            summary={summary}
            onOpenMessageSettings={() => navigate("/settings/follow-up-message")}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-[24px] pb-[8px]">
          <FollowUpTable
            followUps={followUps}
            isLoading={isLoading}
            loadError={loadError}
            busyActions={busyActions}
            onRetry={() => reload()}
            onToggleStatus={handleToggleStatus}
            onOpenDetails={setDetailsFollowUp}
          />
          <Pagination
            paginationProps={{
              pageSize,
              setPageSize,
              dataCount: total,
              setCurrentPage,
              currentPage,
            }}
          />
        </div>
      </div>

      <FollowUpDetailsPanel
        followUp={detailsFollowUp}
        busyAction={detailsFollowUp ? busyActions[detailsFollowUp.rfu_id] : undefined}
        onClose={() => setDetailsFollowUp(null)}
        onCommentAdded={handleCommentAdded}
        onWhatsApp={handleWhatsApp}
        onToggleStatus={handleToggleStatus}
      />
    </InnerLayout>
  );
};

export default CustomerFollowUp;
